import { useEffect, useState } from "react";
import { saveReferral } from "@/lib/referral";
import Lenis from "lenis";
import { FloatingBubble } from "@/components/FloatingBubble";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Services } from "@/components/Services";
import { WorkGallery } from "@/components/WorkGallery";
import { About } from "@/components/About";
import { Testimonials } from "@/components/Testimonials";
import { PortalPromo } from "@/components/PortalPromo";
import { Faq } from "@/components/Faq";
import { Footer } from "@/components/Footer";
import { LazyBookingDialog, bookingPreloadHandlers } from "@/components/LazyBookingDialog";
import { lenisStore } from "@/lib/lenisStore";
import { captureAttribution } from "@/lib/attribution";
import { usePublicSite } from "@/lib/publicSite";
import { useBusinessRating, useSeo } from "@/lib/seo";

// Same as public/index.html — set explicitly so arriving here in-app from a prerendered page
// (/book, a town page) never keeps that page's title/canonical.
const HOME_URL = "https://solidhomeservice.com/";
const HOME_SEO = {
  title: "Handyman in Suquamish & North Kitsap | Solid Home Services",
  description: "Solid Home Services — Bryan's one-man handyman shop in North Kitsap County, WA. Picture hanging, painting, doors, dustless sheetrock repair, trim, shelving, mounting & more. Book a $50 estimate visit online.",
  canonical: HOME_URL,
  ogTitle: "Handyman in Suquamish & North Kitsap | Solid Home Services",
  ogDescription: "One booking. One guy. Every trade. Book a $50 estimate visit online — North Kitsap County, WA. Call or text (360) 930-4399.",
  ogUrl: HOME_URL,
  image: "https://solidhomeservice.com/og-image.png",
};

export default function LandingPage() {
  const [booking, setBooking] = useState({ open: false, service: null, name: "" });
  useSeo(HOME_SEO);
  useBusinessRating(usePublicSite()?.google);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    lenisStore.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisStore.current = null;
    };
  }, []);

  useEffect(() => {
    captureAttribution(); // ad traffic: remember utm/gclid so the booking can be traced
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      saveReferral(ref); // the booking form checks it's a real customer's code before applying it
      window.history.replaceState({}, "", window.location.pathname);
      // Take the referred customer straight to the booking form with the code applied,
      // instead of silently landing on the homepage.
      setBooking({ open: true, service: null, mode: "estimate" });
      return;
    }
    // Portal "Need me again?" links land here — open the Continue My Home Improvement form directly,
    // with the customer's name already filled in from their portal
    if (params.get("request") === "work") {
      setBooking({ open: true, service: null, mode: "work", name: (params.get("name") || "").slice(0, 120), portalToken: (params.get("portal") || "").slice(0, 80) });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // links like /#faq (and the /faq redirect) land on that section; a second pass corrects late layout shifts
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    const jump = () => {
      const el = document.getElementById(id);
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY - 72;
      if (lenisStore.current) lenisStore.current.scrollTo(y, { immediate: true });
      else window.scrollTo(0, y);
    };
    const timers = [400, 1200].map((ms) => setTimeout(jump, ms));
    return () => timers.forEach(clearTimeout);
  }, []);

  // opts: { date } picks a day, { custom } opens straight into "Request a custom time"
  const openBooking = (service, mode, opts = {}) =>
    setBooking({ open: true, service, mode: mode || "estimate", date: opts.date || "", custom: !!opts.custom });

  return (
    <div data-testid="landing-page" className="overflow-x-hidden" {...bookingPreloadHandlers}>
      <Navbar onBook={openBooking} />
      <main>
        <Hero onBook={openBooking} />
        <Services onBook={openBooking} />
        <WorkGallery />
        <About onBook={openBooking} />
        <Testimonials />
        <PortalPromo />
        <Faq onBook={openBooking} />
      </main>
      <Footer onBook={openBooking} />
      <LazyBookingDialog
        open={booking.open}
        service={booking.service}
        mode={booking.mode}
        initialName={booking.name}
        initialDate={booking.date}
        initialCustom={booking.custom}
        initialPortalToken={booking.portalToken}
        onOpenChange={(open) => setBooking((b) => ({ ...b, open }))}
      />
      <FloatingBubble onBook={() => openBooking(null)} hidden={booking.open} heroSelector='[data-testid="hero-section"]' />
    </div>
  );
}
